/**
 * industryPricingDefaults.ts — Sensible pricing defaults per industry.
 *
 * When a tenant picks an industry in the wizard (or changes it later), these
 * values pre-seed the burden %, utilization factors, target margin, material
 * markup, and a suggested overhead/equipment kit. They're starting points,
 * not gospel — the user can change anything. The point is that a brand-new
 * tenant doesn't start with all-zero data that produces a $0 break-even.
 */

import type { IndustryType } from './industryConfig';
import type { OverheadItem, OverheadEquipment } from '../types';

export interface IndustryPricingDefaults {
  /** Typical fully-loaded burden — FICA (7.65%) + FUTA/SUTA + workers' comp + benefits. */
  burdenPercent: number;
  /** % of season hours available after PTO/holidays/weather. */
  availablePercent: number;
  /** Of available, % actually on a paying job. */
  billablePercent: number;
  targetMarginPercent: number;
  defaultMaterialMarkupPercent: number;
  /** Seed overhead items the user can accept or delete. Amounts default to $0
   *  so they don't fake the numbers — the user must fill them in.
   *
   *  EL COMBUSTIBLE VIVE AQUÍ, Y SOLO AQUÍ.
   *
   *  Antes había dos sitios donde meterlo: este renglón y una casilla de
   *  gasolina en cada máquina. Diez talleres del corpus acabaron pagándolo por
   *  los dos lados —uno metió los mismos 4.800 $ en ambos— y su equilibrio
   *  salía 11,4 % alto. Se quitó la casilla de la máquina: el contratista ve
   *  una factura de combustible al mes, no un consumo por máquina, así que un
   *  solo renglón es además como lo tiene en la cabeza. Veintiocho talleres ya
   *  lo llevaban así y solo tres al revés.
   *
   *  WORKERS' COMP TAMPOCO VA AQUÍ. El porcentaje de cargas ya lo lleva dentro
   *  (ver `burdenPercent` abajo y services/burden.ts). Por eso los renglones de
   *  seguro dicen «sin workers comp»: la factura del seguro hay que partirla,
   *  no teclearla entera. */
  suggestedOverhead: Pick<OverheadItem, 'name' | 'frequency'>[];
  suggestedEquipment: Pick<OverheadEquipment, 'name' | 'usefulLifeYears'>[];
}

const DEFAULTS: Record<IndustryType, IndustryPricingDefaults> = {
  landscaping: {
    burdenPercent: 25,
    availablePercent: 80,     // shorter season, more weather days
    billablePercent: 65,
    targetMarginPercent: 28,
    defaultMaterialMarkupPercent: 35,
    suggestedOverhead: [
      { name: 'Rent / Shop', frequency: 'Monthly' },
      { name: 'General Liability (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Software / Subscriptions', frequency: 'Monthly' },
      { name: 'Marketing / Ads', frequency: 'Monthly' },
    ],
    suggestedEquipment: [
      { name: 'Pickup Truck', usefulLifeYears: 8 },
      { name: 'Trailer', usefulLifeYears: 10 },
      { name: 'Commercial Mower', usefulLifeYears: 5 },
      { name: 'Skid-Steer', usefulLifeYears: 7 },
    ],
  },
  general_contractor: {
    burdenPercent: 28,
    availablePercent: 90,
    billablePercent: 70,
    targetMarginPercent: 25,
    defaultMaterialMarkupPercent: 30,
    suggestedOverhead: [
      { name: 'Office / Shop Rent', frequency: 'Monthly' },
      { name: 'General Liability (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Software / Subscriptions', frequency: 'Monthly' },
      { name: 'Licensing / Permits', frequency: 'Annual' },
    ],
    suggestedEquipment: [
      { name: 'Pickup Truck', usefulLifeYears: 8 },
      { name: 'Trailer', usefulLifeYears: 10 },
      { name: 'Power Tools Set', usefulLifeYears: 5 },
    ],
  },
  concrete: {
    burdenPercent: 30,        // higher WC for concrete
    availablePercent: 85,
    billablePercent: 70,
    targetMarginPercent: 25,
    defaultMaterialMarkupPercent: 25,
    suggestedOverhead: [
      { name: 'Shop / Yard', frequency: 'Monthly' },
      { name: 'General Liability (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Software', frequency: 'Monthly' },
    ],
    suggestedEquipment: [
      { name: 'Concrete Truck / Mixer', usefulLifeYears: 8 },
      { name: 'Power Trowel', usefulLifeYears: 7 },
      { name: 'Trailer', usefulLifeYears: 10 },
    ],
  },
  electrical: {
    burdenPercent: 30,
    availablePercent: 92,
    billablePercent: 72,
    targetMarginPercent: 30,
    defaultMaterialMarkupPercent: 40,
    suggestedOverhead: [
      { name: 'Shop', frequency: 'Monthly' },
      { name: 'General Liability + fianza (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Licensing / Continuing Ed', frequency: 'Annual' },
    ],
    suggestedEquipment: [
      { name: 'Service Van', usefulLifeYears: 8 },
      { name: 'Tools / Tester Set', usefulLifeYears: 5 },
    ],
  },
  plumbing: {
    burdenPercent: 30,
    availablePercent: 92,
    billablePercent: 72,
    targetMarginPercent: 30,
    defaultMaterialMarkupPercent: 40,
    suggestedOverhead: [
      { name: 'Shop', frequency: 'Monthly' },
      { name: 'General Liability + fianza (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Licensing', frequency: 'Annual' },
    ],
    suggestedEquipment: [
      { name: 'Service Van', usefulLifeYears: 8 },
      { name: 'Drain Snake / Camera', usefulLifeYears: 7 },
    ],
  },
  painting: {
    burdenPercent: 22,
    availablePercent: 88,
    billablePercent: 70,
    targetMarginPercent: 30,
    defaultMaterialMarkupPercent: 35,
    suggestedOverhead: [
      { name: 'Shop / Storage', frequency: 'Monthly' },
      { name: 'General Liability (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
    ],
    suggestedEquipment: [
      { name: 'Pickup Truck', usefulLifeYears: 8 },
      { name: 'Sprayer / Compressor', usefulLifeYears: 6 },
      { name: 'Scaffolding / Ladders', usefulLifeYears: 10 },
    ],
  },
  cleaning: {
    burdenPercent: 20,
    availablePercent: 92,
    billablePercent: 80,
    targetMarginPercent: 25,
    defaultMaterialMarkupPercent: 25,
    suggestedOverhead: [
      { name: 'Office', frequency: 'Monthly' },
      { name: 'General Liability + fianza (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Supplies (recurring)', frequency: 'Monthly' },
    ],
    suggestedEquipment: [
      { name: 'Service Vehicle', usefulLifeYears: 8 },
      { name: 'Commercial Vacuum / Equipment', usefulLifeYears: 5 },
    ],
  },
  hvac: {
    burdenPercent: 30,
    availablePercent: 90,
    billablePercent: 72,
    targetMarginPercent: 30,
    defaultMaterialMarkupPercent: 40,
    suggestedOverhead: [
      { name: 'Shop', frequency: 'Monthly' },
      { name: 'General Liability + fianza (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Licensing / EPA', frequency: 'Annual' },
    ],
    suggestedEquipment: [
      { name: 'Service Van', usefulLifeYears: 8 },
      { name: 'Recovery Machine / Tools', usefulLifeYears: 7 },
    ],
  },
  roofing: {
    burdenPercent: 35,        // highest WC class
    availablePercent: 80,     // weather-dependent
    billablePercent: 70,
    targetMarginPercent: 28,
    defaultMaterialMarkupPercent: 30,
    suggestedOverhead: [
      { name: 'Shop / Yard', frequency: 'Monthly' },
      { name: 'General Liability (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Dumpster / Disposal', frequency: 'Monthly' },
    ],
    suggestedEquipment: [
      { name: 'Pickup Truck', usefulLifeYears: 8 },
      { name: 'Trailer', usefulLifeYears: 10 },
      { name: 'Safety / Harness Gear', usefulLifeYears: 5 },
    ],
  },
  custom: {
    burdenPercent: 25,
    availablePercent: 85,
    billablePercent: 70,
    targetMarginPercent: 25,
    defaultMaterialMarkupPercent: 30,
    suggestedOverhead: [
      { name: 'Rent', frequency: 'Monthly' },
      { name: 'General Liability (sin workers comp)', frequency: 'Monthly' },
      { name: 'Combustible (toda la flota)', frequency: 'Monthly' },
      { name: 'Software', frequency: 'Monthly' },
    ],
    suggestedEquipment: [],
  },
};

export function getIndustryPricingDefaults(type?: string | null): IndustryPricingDefaults {
  if (!type) return DEFAULTS.landscaping;
  return DEFAULTS[type as IndustryType] || DEFAULTS.custom;
}
